/**
 * StageOverrideDialog
 * ───────────────────
 * Proof-based gate for manual stage transitions.
 *
 * Why this exists:
 *   Stage is the highest-risk field on a Lead/Opportunity — it gates revenue
 *   recognition, dispatches automations (notifications, SLAs, follow-ups),
 *   and writes to the lifecycle. A misclick on a chip or a bad drag should
 *   never silently advance the pipeline.
 *
 * Per Core rules:
 *   - "Proof-based execution. No direct state toggles."
 *   - "High-risk changes require typed 'CONFIRM' or workflows."
 *   - "Strict status ownership."
 *
 * The natural path forward is to let the workflow advance the stage as the
 * rep completes proof (sent quote → Quote Sent, signed → Closed Won, etc.).
 * This dialog is the *override* path — admin/manager only, requires a typed
 * "CONFIRM" plus a reason, and is captured for audit.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import type { UserRole } from "@/data/RoleContext";

/** Roles allowed to override stage. Sales reps are intentionally excluded — they advance via workflow proofs. */
export const STAGE_OVERRIDE_ROLES: UserRole[] = [
  "super_admin",
  "owner",
  "ops_manager",
  "admin",
  "it_staff",
  "sales_manager",
];

export function canOverrideStage(role: UserRole): boolean {
  return STAGE_OVERRIDE_ROLES.includes(role);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fromStage: string;
  toStage: string;
  entityLabel?: string; // e.g. "lead", "opportunity"
  entityName?: string;
  onConfirm: (reason: string) => void;
}

export function StageOverrideDialog({
  open, onOpenChange, fromStage, toStage,
  entityLabel = "record", entityName, onConfirm,
}: Props) {
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const canSubmit = reason.trim().length >= 5 && confirmText.trim().toUpperCase() === "CONFIRM";

  function handleSubmit() {
    if (!canSubmit) return;
    onConfirm(reason.trim());
    setReason("");
    setConfirmText("");
    onOpenChange(false);
  }

  function handleCancel() {
    setReason("");
    setConfirmText("");
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : handleCancel())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-warning/10 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-warning" />
            </div>
            <DialogTitle>Manually override stage?</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            Stage usually advances automatically as work is completed. Manual
            overrides bypass that flow and may fire downstream automations
            (notifications, SLAs, follow-ups). They're recorded for audit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-md border border-border/60 bg-muted/40 p-3 text-sm">
            {entityName && <p className="text-xs text-muted-foreground mb-1">{entityLabel}: <span className="text-foreground font-medium">{entityName}</span></p>}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">{fromStage}</span>
              <span className="text-muted-foreground">→</span>
              <span className="font-medium text-foreground">{toStage}</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="override-reason" className="text-xs">Reason for override <span className="text-destructive">*</span></Label>
            <Textarea
              id="override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer confirmed appointment by phone outside the booking flow"
              rows={3}
              className="text-sm"
            />
            <p className="text-[10px] text-muted-foreground">Minimum 5 characters. Captured in stage history.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="override-confirm" className="text-xs">Type <span className="font-mono font-semibold">CONFIRM</span> to proceed</Label>
            <Input
              id="override-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="CONFIRM"
              className="text-sm font-mono"
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>Override stage</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
